import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Pencil, Mail, Send, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700',
  manager: 'bg-blue-100 text-blue-700',
  staff: 'bg-green-100 text-green-700',
  viewer: 'bg-gray-100 text-gray-700'
};

const FIRMS = ['Apicella', 'MHA'];

export const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'staff',
    firm_access: ['Apicella', 'MHA']
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    role: 'staff',
    firm_access: ['Apicella', 'MHA']
  });
  const [selectedUsersForEmail, setSelectedUsersForEmail] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/auth/register`, formData);
      
      toast.success('User created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'staff',
      firm_access: ['Apicella', 'MHA']
    });
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditFormData({
      name: user.name,
      role: user.role,
      firm_access: user.firm_access || ['Apicella', 'MHA']
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/users/${selectedUser.user_id}`, editFormData);
      
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const toggleFirmAccess = (firm, isEdit = false) => {
    const currentData = isEdit ? editFormData : formData;
    const setData = isEdit ? setEditFormData : setFormData;
    
    const currentAccess = currentData.firm_access || [];
    let newAccess;
    
    if (currentAccess.includes(firm)) {
      // Don't allow removing last firm
      if (currentAccess.length === 1) {
        toast.error('User must have access to at least one firm');
        return;
      }
      newAccess = currentAccess.filter(f => f !== firm);
    } else {
      newAccess = [...currentAccess, firm];
    }
    
    setData({ ...currentData, firm_access: newAccess });
  };

  const toggleUserEmailSelection = (userId) => {
    setSelectedUsersForEmail(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsersForEmail = () => {
    if (selectedUsersForEmail.length === users.length) {
      setSelectedUsersForEmail([]);
    } else {
      setSelectedUsersForEmail(users.map(u => u.user_id));
    }
  };

  const handlePreviewEmail = async () => {
    try {
      setLoadingPreview(true);
      const response = await axios.get(`${API}/admin/preview-financial-email`);
      setEmailPreview(response.data);
      setIsPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load email preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendEmails = async () => {
    if (selectedUsersForEmail.length === 0) {
      toast.error('Please select at least one user to send the email to');
      return;
    }

    try {
      setSendingEmails(true);
      const response = await axios.post(`${API}/admin/send-weekly-emails`, {
        user_ids: selectedUsersForEmail
      });
      
      // Show detailed results
      const { results } = response.data;
      const successCount = results.filter(r => r.success).length;
      const failedEmails = results.filter(r => !r.success);
      
      if (successCount === results.length) {
        toast.success(`Financial summary sent to ${successCount} user(s) successfully!`);
      } else if (successCount > 0) {
        toast.warning(`Sent to ${successCount}/${results.length} users. Failed: ${failedEmails.map(r => r.email).join(', ')}`);
      } else {
        // Show detailed error message
        const errorMsg = failedEmails[0]?.error || 'Unknown error';
        if (errorMsg.includes('domain is not verified')) {
          toast.error('Email domain not verified in Resend. Please verify your domain at resend.com/domains');
        } else {
          toast.error(`Failed to send emails: ${errorMsg}`);
        }
      }
      
      setSelectedUsersForEmail([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="users-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight">User Management</h1>
            <p className="text-foreground-muted mt-2">Manage user accounts and permissions</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent hover:bg-accent-hover text-accent-foreground" data-testid="add-user-button">
                <Plus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-2xl font-sans">Invite New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="user-form">
                <div className="space-y-2">
                  <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Temporary password"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role <span className="text-red-500">*</span></Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({...formData, role: value})}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Firm Access <span className="text-red-500">*</span></Label>
                  <p className="text-xs text-foreground-muted mb-2">Select which firms this user can access</p>
                  <div className="flex gap-4">
                    {FIRMS.map((firm) => (
                      <div key={firm} className="flex items-center space-x-2">
                        <Checkbox
                          id={`firm-${firm}`}
                          checked={formData.firm_access?.includes(firm)}
                          onCheckedChange={() => toggleFirmAccess(firm, false)}
                        />
                        <Label htmlFor={`firm-${firm}`} className="text-sm font-normal cursor-pointer">
                          {firm}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                  <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-user">
                    Create User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Email Actions Bar */}
        {currentUser?.role === 'admin' && users.length > 0 && (
          <Card className="border border-border shadow-sm p-4 mb-6 bg-blue-50/50" data-testid="email-actions-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-slate-800">Send Financial Summary Email</p>
                  <p className="text-sm text-slate-500">
                    {selectedUsersForEmail.length === 0 
                      ? 'Select users below to send them the weekly financial summary'
                      : `${selectedUsersForEmail.length} user(s) selected`
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePreviewEmail}
                  disabled={loadingPreview}
                  variant="outline"
                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  data-testid="preview-email-button"
                >
                  {loadingPreview ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  Preview Email
                </Button>
                <Button
                  onClick={handleSendEmails}
                  disabled={selectedUsersForEmail.length === 0 || sendingEmails}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-emails-button"
                >
                  {sendingEmails ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {sendingEmails ? 'Sending...' : 'Send Email'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Users Table */}
        <Card className="border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : users.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    {currentUser?.role === 'admin' && (
                      <th className="px-4 py-4 text-center w-12">
                        <Checkbox
                          checked={selectedUsersForEmail.length === users.length && users.length > 0}
                          onCheckedChange={selectAllUsersForEmail}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Role</th>
                    <th className="px-6 py-4 text-left">Firm Access</th>
                    <th className="px-6 py-4 text-left">Created</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((user) => (
                    <tr 
                      key={user.user_id} 
                      className={`hover:bg-slate-50/50 transition-colors ${selectedUsersForEmail.includes(user.user_id) ? 'bg-blue-50/50' : ''}`}
                      data-testid={`user-row-${user.user_id}`}
                    >
                      {currentUser?.role === 'admin' && (
                        <td className="px-4 py-4 text-center">
                          <Checkbox
                            checked={selectedUsersForEmail.includes(user.user_id)}
                            onCheckedChange={() => toggleUserEmailSelection(user.user_id)}
                            data-testid={`select-user-${user.user_id}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 font-medium text-foreground">{user.name}</td>
                      <td className="px-6 py-4 text-foreground-muted">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          ROLE_COLORS[user.role] || 'bg-gray-100 text-gray-700'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          {(user.firm_access || ['Apicella', 'MHA']).map((firm) => (
                            <span 
                              key={firm}
                              className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs"
                            >
                              {firm}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-foreground-muted text-xs">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          {user.user_id !== currentUser.user_id && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditClick(user)}
                                data-testid={`edit-user-${user.user_id}`}
                              >
                                <Pencil className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUser(user.user_id)}
                                className="text-destructive hover:text-destructive hover:bg-red-50"
                                data-testid={`delete-user-${user.user_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-users">
              <p className="text-foreground-muted">No users found</p>
            </div>
          )}
        </Card>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans">Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-user-form">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Name <span className="text-red-500">*</span></Label>
                <Input
                  id="edit_name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  placeholder="Full name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_role">Role <span className="text-red-500">*</span></Label>
                <Select 
                  value={editFormData.role} 
                  onValueChange={(value) => setEditFormData({...editFormData, role: value})}
                >
                  <SelectTrigger data-testid="edit-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Firm Access <span className="text-red-500">*</span></Label>
                <p className="text-xs text-foreground-muted mb-2">Select which firms this user can access</p>
                <div className="flex gap-4">
                  {FIRMS.map((firm) => (
                    <div key={firm} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-firm-${firm}`}
                        checked={editFormData.firm_access?.includes(firm)}
                        onCheckedChange={() => toggleFirmAccess(firm, true)}
                      />
                      <Label htmlFor={`edit-firm-${firm}`} className="text-sm font-normal cursor-pointer">
                        {firm}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button type="button" variant="secondary" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-edit-user">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Email Preview Dialog */}
        <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Email Preview
              </DialogTitle>
            </DialogHeader>
            {emailPreview && (
              <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                {/* Email metadata */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex gap-2 text-sm">
                    <span className="text-slate-500 font-medium w-20">From:</span>
                    <span className="text-slate-700">{emailPreview.from}</span>
                  </div>
                  <div className="flex gap-2 text-sm">
                    <span className="text-slate-500 font-medium w-20">Subject:</span>
                    <span className="text-slate-700">{emailPreview.subject}</span>
                  </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-blue-600 font-medium">Total Claims</p>
                    <p className="text-lg font-bold text-blue-700">{emailPreview.summary?.total_claims || 0}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-green-600 font-medium">Paid in Full</p>
                    <p className="text-lg font-bold text-green-700">{emailPreview.summary?.paid_in_full || 0}</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-red-600 font-medium">Outstanding</p>
                    <p className="text-lg font-bold text-red-700">
                      ${(emailPreview.summary?.total_outstanding || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <p className="text-xs text-purple-600 font-medium">New This Week</p>
                    <p className="text-lg font-bold text-purple-700">{emailPreview.summary?.new_claims_this_week || 0}</p>
                  </div>
                </div>

                {/* HTML preview iframe */}
                <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden min-h-[300px]">
                  <iframe
                    srcDoc={emailPreview.html_preview}
                    title="Email Preview"
                    className="w-full h-full min-h-[300px]"
                    style={{ border: 'none' }}
                    data-testid="email-preview-iframe"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setIsPreviewDialogOpen(false)}
                  >
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};